import { proxyCustomElement, HTMLElement, createEvent, h, Host } from '@stencil/core/internal/client';
import { s as setInputRef, g as getError, a as getValidityObject, c as checkValidity, r as reportValidity, b as setCustomValidity, w as watchError, d as watchWarning, e as getHostAttributes, f as getFormFieldAttributes } from './index7.js';
import { a as isKeyOfObject } from './index12.js';
import { W as WuiDateRangeInput, d as defineCustomElement$k } from './date-range-input.js';
import { D as DATE_RANGE_PRESETS } from './constants.js';
import { d as defineCustomElement$r } from './badge.js';
import { d as defineCustomElement$q } from './box.js';
import { d as defineCustomElement$p } from './button.js';
import { d as defineCustomElement$o } from './check-input.js';
import { d as defineCustomElement$n } from './close-button.js';
import { d as defineCustomElement$m } from './date-input.js';
import { f as format, d as defineCustomElement$l } from './date-input-calendar.js';
import { d as defineCustomElement$j } from './divider.js';
import { d as defineCustomElement$i } from './form-field.js';
import { d as defineCustomElement$h } from './icon.js';
import { d as defineCustomElement$g } from './input.js';
import { d as defineCustomElement$f } from './input-button.js';
import { d as defineCustomElement$e } from './input-menu.js';
import { d as defineCustomElement$d } from './layout.js';
import { d as defineCustomElement$c } from './list.js';
import { d as defineCustomElement$b } from './list-item.js';
import { d as defineCustomElement$a } from './list-item-divider.js';
import { d as defineCustomElement$9 } from './list-item-header.js';
import { d as defineCustomElement$8 } from './list-item-meta.js';
import { d as defineCustomElement$7 } from './loader.js';
import { d as defineCustomElement$6 } from './menu-pane.js';
import { d as defineCustomElement$5 } from './select-input.js';
import { d as defineCustomElement$4 } from './status.js';
import { d as defineCustomElement$3 } from './tooltip.js';
import { d as defineCustomElement$2 } from './tooltip-label.js';

const dateRangeInputWithPresetsCss = ":host{-webkit-font-smoothing:antialiased;-moz-osx-font-smoothing:grayscale;box-sizing:border-box;font-family:Open Sans;font-size:.875rem;font-weight:400;letter-spacing:-.0004em;line-height:1.25rem;line-height:normal}:host *,:host :after,:host :before{box-sizing:inherit}:host img{flex:none;overflow:hidden;text-overflow:ellipsis;white-space:pre-line}wui-select-input{max-width:var(--jw-container-xs)}";
const WuiDateRangeInputWithPresetsStyle0 = dateRangeInputWithPresetsCss;

const WuiDateRangeInputWithPresets$1 = /*@__PURE__*/ proxyCustomElement(class WuiDateRangeInputWithPresets extends HTMLElement {
    constructor() {
        super();
        this.__registerHost();
        this.__attachShadow();
        this.validityChecked = createEvent(this, "validityChecked", 7);
        this.validityReported = createEvent(this, "validityReported", 7);
        this.setInputRef = setInputRef(this);
        this.dirty = false;
        this.getError = getError(this);
        this.handlePresetChange = (e) => {
            // We fire our own input event
            e.stopPropagation();
            e.stopImmediatePropagation();
            const newPreset = e.target?.value;
            if (!isKeyOfObject(newPreset, this.presets)) {
                return;
            }
            this.selectedPreset = newPreset;
            const foundPreset = this.presets[newPreset];
            this.updateValue(WuiDateRangeInput.stringifyValue(foundPreset.value()));
        };
        this.handleDateRangeChange = (e) => {
            // We fire our own input event
            e.stopPropagation();
            e.stopImmediatePropagation();
            if (this.selectedPreset !== 'CUSTOM') {
                return;
            }
            const value = e.target?.value;
            if (!value) {
                return;
            }
            this.updateValue(value);
        };
        this.value = undefined;
        this.required = false;
        this.error = undefined;
        this.warning = undefined;
        this.validate = undefined;
        this.validity = getValidityObject();
        this.validationMessage = '';
        this.customValidation = undefined;
        this.checkValidity = checkValidity(this);
        this.reportValidity = reportValidity(this);
        this.setCustomValidity = setCustomValidity(this);
        this.showValidity = false;
        this.formField = undefined;
        this.name = undefined;
        this.disabled = undefined;
        this.showDescription = false;
        this.startDateLabel = true;
        this.endDateLabel = true;
        this.presetSelectLabel = 'Timeframe';
        this.presets = DATE_RANGE_PRESETS;
        this.selectedPreset = 'CUSTOM';
    }
    _watchError(...args) {
        watchError(this, ...args);
    }
    _watchWarning(...args) {
        watchWarning(this, ...args);
    }
    get valueAsDateRange() {
        return WuiDateRangeInput.parseValue(this.value);
    }
    set valueAsDateRange(newValue) {
        this.value = newValue ? WuiDateRangeInput.stringifyValue(newValue) : undefined;
        this.selectedPreset = 'CUSTOM';
        this.dirty = true;
        this.checkValidity();
    }
    get valueAsPresetName() {
        return this.selectedPreset;
    }
    set valueAsPresetName(newValue) {
        if (isKeyOfObject(newValue, this.presets)) {
            this.value = WuiDateRangeInput.stringifyValue(this.presets[newValue].value());
            this.selectedPreset = newValue;
        }
        else {
            this.value = WuiDateRangeInput.stringifyValue([null, null]);
            this.selectedPreset = 'CUSTOM';
        }
        this.dirty = true;
        this.checkValidity();
    }
    updateValue(newValue) {
        this.value = newValue;
        this.elm.dispatchEvent(new window.Event('input', { bubbles: true }));
        this.dirty = true;
        this.checkValidity();
    }
    getDateRangeDescription() {
        if (!this.valueAsDateRange) {
            return '';
        }
        const [start, end] = this.valueAsDateRange;
        if (!(start && end)) {
            return '';
        }
        const startDate = new Date(start);
        const endDate = new Date(end);
        const isSameYear = startDate.getFullYear() === endDate.getFullYear();
        // Format start date differently based on whether years match
        const formattedStart = isSameYear ? format(startDate, 'MMM dd') : format(startDate, "MMM dd ''yy");
        const formattedEnd = format(endDate, "MMM dd ''yy");
        return `Chosen dates: ${formattedStart} - ${formattedEnd}`;
    }
    render() {
        return (h(Host, { key: '1b27438f57bad5b61d4c93316d0f024cc9d8a3a8', ...getHostAttributes(this) }, h("wui-form-field", { key: 'bbf8825fbedc015fe9d32891f68d6a1dd1b615eb', ...getFormFieldAttributes(this), description: this.showDescription ? this.getDateRangeDescription() : undefined }, h("wui-layout", { key: '4c6c4cb0e19ae4eaf30c105c750a15cdf74e0adc', type: "field-field-group" }, h("wui-select-input", { key: '6212370feca275ee8f31354a7c9e7175be2acee6', options: Object.entries(this.presets).map(([presetName, preset]) => {
                return {
                    label: preset.label,
                    value: presetName,
                };
            }), value: this.selectedPreset, onInput: this.handlePresetChange, disabled: this.disabled, formField: {
                label: this.presetSelectLabel,
            } }), h("wui-date-range-input", { key: '5fc6b150b07d22503267de6f8241f2e7c062525a', startDateLabel: this.startDateLabel, endDateLabel: this.endDateLabel, disabled: this.disabled || this.selectedPreset !== 'CUSTOM', value: this.value, onInput: this.handleDateRangeChange })))));
    }
    get elm() { return this; }
    static get watchers() { return {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }; }
    static get style() { return WuiDateRangeInputWithPresetsStyle0; }
}, [1, "wui-date-range-input-with-presets", {
        "value": [1025],
        "required": [8],
        "error": [8],
        "warning": [8],
        "validate": [16],
        "validity": [1040],
        "validationMessage": [1025, "validation-message"],
        "checkValidity": [16],
        "reportValidity": [16],
        "setCustomValidity": [16],
        "formField": [16],
        "name": [1],
        "disabled": [4],
        "showDescription": [4, "show-description"],
        "startDateLabel": [8, "start-date-label"],
        "endDateLabel": [8, "end-date-label"],
        "presetSelectLabel": [1, "preset-select-label"],
        "presets": [16],
        "customValidation": [32],
        "showValidity": [32],
        "selectedPreset": [32]
    }, undefined, {
        "error": ["_watchError"],
        "warning": ["_watchWarning"]
    }]);
WuiDateRangeInputWithPresets$1.PRESETS = DATE_RANGE_PRESETS;
function defineCustomElement$1() {
    if (typeof customElements === "undefined") {
        return;
    }
    const components = ["wui-date-range-input-with-presets", "wui-badge", "wui-box", "wui-button", "wui-check-input", "wui-close-button", "wui-date-input", "wui-date-input-calendar", "wui-date-range-input", "wui-divider", "wui-form-field", "wui-icon", "wui-input", "wui-input-button", "wui-input-menu", "wui-layout", "wui-list", "wui-list-item", "wui-list-item-divider", "wui-list-item-header", "wui-list-item-meta", "wui-loader", "wui-menu-pane", "wui-select-input", "wui-status", "wui-tooltip", "wui-tooltip-label"];
    components.forEach(tagName => { switch (tagName) {
        case "wui-date-range-input-with-presets":
            if (!customElements.get(tagName)) {
                customElements.define(tagName, WuiDateRangeInputWithPresets$1);
            }
            break;
        case "wui-badge":
            if (!customElements.get(tagName)) {
                defineCustomElement$r();
            }
            break;
        case "wui-box":
            if (!customElements.get(tagName)) {
                defineCustomElement$q();
            }
            break;
        case "wui-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$p();
            }
            break;
        case "wui-check-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$o();
            }
            break;
        case "wui-close-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$n();
            }
            break;
        case "wui-date-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$m();
            }
            break;
        case "wui-date-input-calendar":
            if (!customElements.get(tagName)) {
                defineCustomElement$l();
            }
            break;
        case "wui-date-range-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$k();
            }
            break;
        case "wui-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$j();
            }
            break;
        case "wui-form-field":
            if (!customElements.get(tagName)) {
                defineCustomElement$i();
            }
            break;
        case "wui-icon":
            if (!customElements.get(tagName)) {
                defineCustomElement$h();
            }
            break;
        case "wui-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$g();
            }
            break;
        case "wui-input-button":
            if (!customElements.get(tagName)) {
                defineCustomElement$f();
            }
            break;
        case "wui-input-menu":
            if (!customElements.get(tagName)) {
                defineCustomElement$e();
            }
            break;
        case "wui-layout":
            if (!customElements.get(tagName)) {
                defineCustomElement$d();
            }
            break;
        case "wui-list":
            if (!customElements.get(tagName)) {
                defineCustomElement$c();
            }
            break;
        case "wui-list-item":
            if (!customElements.get(tagName)) {
                defineCustomElement$b();
            }
            break;
        case "wui-list-item-divider":
            if (!customElements.get(tagName)) {
                defineCustomElement$a();
            }
            break;
        case "wui-list-item-header":
            if (!customElements.get(tagName)) {
                defineCustomElement$9();
            }
            break;
        case "wui-list-item-meta":
            if (!customElements.get(tagName)) {
                defineCustomElement$8();
            }
            break;
        case "wui-loader":
            if (!customElements.get(tagName)) {
                defineCustomElement$7();
            }
            break;
        case "wui-menu-pane":
            if (!customElements.get(tagName)) {
                defineCustomElement$6();
            }
            break;
        case "wui-select-input":
            if (!customElements.get(tagName)) {
                defineCustomElement$5();
            }
            break;
        case "wui-status":
            if (!customElements.get(tagName)) {
                defineCustomElement$4();
            }
            break;
        case "wui-tooltip":
            if (!customElements.get(tagName)) {
                defineCustomElement$3();
            }
            break;
        case "wui-tooltip-label":
            if (!customElements.get(tagName)) {
                defineCustomElement$2();
            }
            break;
    } });
}
defineCustomElement$1();

const WuiDateRangeInputWithPresets = WuiDateRangeInputWithPresets$1;
const defineCustomElement = defineCustomElement$1;

export { WuiDateRangeInputWithPresets, defineCustomElement };

//# sourceMappingURL=wui-date-range-input-with-presets.js.map